import * as React from "react";
import {useState} from "react";
import {DialogEx} from "@/lib/components/DialogEx";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {useTranslation} from "react-i18next";
import {Alert, Grid2, Skeleton, TextField, Typography} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {AdminI18N, CommonI18N} from "@/translations";
import dayjs from "dayjs";
import {DateFormatter} from "@/lib/components/DateFormate";

type Props = {
    open: boolean;
    onClose: () => void;
    title: string;
    link: string | undefined;
    validUntil: string | undefined;
    subtitle?: string;
};

/**
 * Generic dialog for displaying a generated link with a copy button and optional validity date.
 * Supports a loading state (link === undefined) while the link is being generated.
 */
export const LinkDisplayDialog = ({open, onClose, title, link, validUntil, subtitle}: Props) => {
    const {t} = useTranslation();
    const [copySuccess, setCopySuccess] = useState(false);
    const dateFormat = DateFormatter("date_hm");

    const handleCopy = async () => {
        if (!link) {
            return;
        }
        try {
            if (navigator.clipboard && globalThis.isSecureContext) {
                await navigator.clipboard.writeText(link);
            } else {
                // Fallback for non-secure contexts (e.g. HTTP dev environments)
                const textArea = document.createElement("textarea");
                textArea.value = link;
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand("copy");
                textArea.remove();
            }
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 3000);
        } catch (error: unknown) {
            console.error("Failed to copy link to clipboard", error);
        }
    };

    const buttons = (
            <>
                <ButtonEx
                        label={t(AdminI18N.linkDisplay.action.copy)}
                        color="primary"
                        startIcon={<ContentCopyIcon/>}
                        onClick={handleCopy}
                        disabled={!link}
                />
                <ButtonEx label={t(CommonI18N.action.close)} color="secondary" onClick={onClose}/>
            </>
    );

    return (
            <DialogEx open={open} onClose={onClose} title={title} buttons={buttons} maxWidth="sm" fullWidth>
                <Grid2 container spacing={2} padding={1}>
                    {subtitle && (
                            <Grid2 size={{xs: 12}}>
                                <Alert severity="warning">{subtitle}</Alert>
                            </Grid2>
                    )}
                    <Grid2 size={{xs: 12}}>
                        {link === undefined ? (
                                <Skeleton variant="rectangular" height={40}/>
                        ) : (
                                <TextField
                                        value={link}
                                        fullWidth
                                        slotProps={{input: {readOnly: true}}}
                                        variant="outlined"
                                        size="small"
                                />
                        )}
                    </Grid2>
                    {validUntil && (
                            <Grid2 size={{xs: 12}}>
                                <Typography variant="body2">
                                    {t(AdminI18N.linkDisplay.field.validUntil)}: {dayjs(validUntil).format(dateFormat)}
                                </Typography>
                            </Grid2>
                    )}
                    {copySuccess && (
                            <Grid2 size={{xs: 12}}>
                                <Alert severity="success">{t(AdminI18N.linkDisplay.message.copySuccess)}</Alert>
                            </Grid2>
                    )}
                </Grid2>
            </DialogEx>
    );
};
